// v10/src/components/PreflightLog.js - EQUAL HEIGHT fields
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Building2, Plane } from 'lucide-react';
import { defaultSession } from '../mock/aircraftData';

const PreflightLog = ({ sessionData, onUpdateSession, darkMode }) => {
  const getCurrentDateTime = () => {
    const now = new Date();
    return {
      time: `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`,
      date: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`
    };
  };

  const [localSession, setLocalSession] = useState(() => {
    const now = getCurrentDateTime();
    return { ...defaultSession, ...sessionData, time: sessionData.time || now.time, date: sessionData.date || now.date };
  });

  useEffect(() => {
    if (!sessionData?.time || !sessionData?.date) {
      const now = getCurrentDateTime();
      const updated = { ...localSession, time: localSession.time || now.time, date: localSession.date || now.date };
      setLocalSession(updated);
      onUpdateSession(updated);
    }
  }, [sessionData]);

  const handleUpdate = (key, value) => {
    const updated = { ...localSession, [key]: value };
    setLocalSession(updated);
    onUpdateSession(updated);
  };

  const safeTimeValue = React.useMemo(() => {
    if (typeof localSession.time === 'string' && /^\d{2}:\d{2}$/.test(localSession.time))
      return localSession.time;
    return getCurrentDateTime().time;
  }, [localSession.time]);

  const card = `rounded-2xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`;

  // ✅ mobile: text-xs px-1.5 | desktop (sm+): text-sm px-3
  const inputClass = [
    'w-full h-10 border rounded-xl',
    'text-xs sm:text-sm',
    'px-1.5 sm:px-3',
    'focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all',
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
  ].join(' ');

  const labelClass = `flex items-center gap-1 sm:gap-1.5 text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const iconClass  = `w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`;

  return (
    <motion.div
      className={`${card} p-4 mb-4`}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}
    >
      {localSession.aircraft && (
        <div className={`flex items-center gap-2 mb-3 pb-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className={`p-1.5 rounded-lg flex-shrink-0 ${darkMode ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
            <Plane className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
            <p className={`text-xs leading-none mb-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Aeronave</p>
            <p className={`text-sm font-bold leading-none ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              {localSession.aircraft.name || `${localSession.aircraft.manufacturer} ${localSession.aircraft.model_name}`}
            </p>
          </div>
        </div>
      )}

      {/* ✅ mobile: gap-2 min-w-0 | desktop: gap-3 — evita que los campos se corten */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="flex flex-col min-w-0">
          <label className={labelClass}><Calendar className={iconClass} /> Fecha</label>
          <input
            type="date"
            value={localSession.date}
            onChange={e => handleUpdate('date', e.target.value)}
            className={inputClass}
            style={{ colorScheme: darkMode ? 'dark' : 'light' }}
          />
        </div>
        <div className="flex flex-col min-w-0">
          <label className={labelClass}><Clock className={iconClass} /> Hora</label>
          <input
            type="time"
            value={safeTimeValue}
            step="60"
            onChange={e => handleUpdate('time', e.target.value)}
            className={inputClass}
            style={{ colorScheme: darkMode ? 'dark' : 'light' }}
          />
        </div>
        <div className="flex flex-col min-w-0">
          <label className={labelClass}><Building2 className={iconClass} /> Aeroclub</label>
          <input
            type="text"
            placeholder="Aeroclub..."
            value={localSession.clubName}
            onChange={e => handleUpdate('clubName', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default PreflightLog;
